/**
 * Components that render the content documents in `/content`. The documents keep the
 * document shape the site was originally exported with — rich text as an array of blocks
 * with `spans`, images as a field object — so these components read that shape directly.
 * Nothing here fetches: the JSON is imported at build time.
 */

import Image, { type ImageProps } from 'next/image'
import Link from 'next/link'
import type { AnchorHTMLAttributes, ReactNode } from 'react'
import { linkResolver as defaultLinkResolver } from '@/utils/linkResolver'
import type { ImageFieldImage, RichTextField, RTLinkNodeData, RTNode, RTSpan } from './content-fields'

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------

type LinkResolverFn = (doc: { type?: string; uid?: string } | null | undefined) => string

/** Resolves a link field to an href: prefer the field's own resolved url, then a resolver. */
export function asLink(
  field: RTLinkNodeData | null | undefined,
  options?: { linkResolver?: LinkResolverFn }
): string | null {
  if (!field) return null
  if (field.link_type === 'Any' || !field.link_type) return null
  if (field.url) return field.url
  const resolver = options?.linkResolver ?? defaultLinkResolver
  if (field.link_type === 'Document') {
    return resolver({ type: field.type, uid: field.uid })
  }
  return null
}

export interface ContentLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  field?: RTLinkNodeData | null
  href?: string
  linkResolver?: LinkResolverFn
  children?: ReactNode
}

const isExternalHref = (href: string) =>
  /^https?:\/\//.test(href) || href.startsWith('mailto:') || href.startsWith('tel:')

export function ContentLink({
  field,
  href: hrefProp,
  linkResolver,
  children,
  target,
  ...rest
}: ContentLinkProps) {
  const href = hrefProp ?? asLink(field, { linkResolver }) ?? '#'
  const resolvedTarget = target ?? field?.target
  const content = children ?? field?.text ?? null

  if (isExternalHref(href) || resolvedTarget === '_blank') {
    return (
      <a
        href={href}
        target={resolvedTarget}
        rel={resolvedTarget === '_blank' ? 'noopener noreferrer' : undefined}
        {...rest}
      >
        {content}
      </a>
    )
  }

  return (
    <Link href={href} {...rest}>
      {content}
    </Link>
  )
}

// ---------------------------------------------------------------------------
// Images
// ---------------------------------------------------------------------------

export interface ContentImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'width' | 'height'> {
  field?: Partial<ImageFieldImage> | null
  fallbackAlt?: string
}

/** Renders an image field as a plain `<img>`, unoptimized. */
export function ContentImage({ field, fallbackAlt, alt, ...rest }: ContentImageProps) {
  if (!field?.url) return null
  // biome-ignore lint/a11y/useAltText: alt comes from the field or fallbackAlt
  // biome-ignore lint/performance/noImgElement: deliberately unoptimized; use ContentNextImage instead
  return (
    <img
      src={field.url}
      alt={alt ?? field.alt ?? fallbackAlt ?? ''}
      width={field.dimensions?.width}
      height={field.dimensions?.height}
      {...rest}
    />
  )
}

export interface ContentNextImageProps extends Omit<Partial<ImageProps>, 'src' | 'alt'> {
  field?: Partial<ImageFieldImage> | null
  fallbackAlt?: string
  alt?: string
}

/** Renders an image field through `next/image`, taking its dimensions from the field. */
export function ContentNextImage({ field, fallbackAlt, alt, width, height, ...rest }: ContentNextImageProps) {
  if (!field?.url) return null
  const resolvedAlt = alt ?? field.alt ?? fallbackAlt ?? ''
  // `fill` and explicit dimensions are mutually exclusive in next/image.
  if (rest.fill) {
    return <Image src={field.url} alt={resolvedAlt} {...rest} />
  }
  return (
    <Image
      src={field.url}
      alt={resolvedAlt}
      width={width ?? field.dimensions?.width ?? 0}
      height={height ?? field.dimensions?.height ?? 0}
      {...rest}
    />
  )
}

// ---------------------------------------------------------------------------
// Rich text
// ---------------------------------------------------------------------------

type SpanComponent = (props: { children: ReactNode; node: RTSpan; key?: string }) => ReactNode
type BlockComponent = (props: { children: ReactNode; node: RTNode; key?: string }) => ReactNode
type ImageComponent = (props: { node: Extract<RTNode, { type: 'image' }>; key?: string }) => ReactNode

export interface RichTextComponents {
  heading1?: BlockComponent
  heading2?: BlockComponent
  heading3?: BlockComponent
  heading4?: BlockComponent
  heading5?: BlockComponent
  heading6?: BlockComponent
  paragraph?: BlockComponent
  preformatted?: BlockComponent
  list?: (props: { children: ReactNode; key?: string }) => ReactNode
  oList?: (props: { children: ReactNode; key?: string }) => ReactNode
  listItem?: BlockComponent
  oListItem?: BlockComponent
  image?: ImageComponent
  embed?: (props: { node: RTNode; key?: string }) => ReactNode
  strong?: SpanComponent
  em?: SpanComponent
  hyperlink?: SpanComponent
  label?: SpanComponent
}

const defaultComponents: Required<
  Pick<
    RichTextComponents,
    | 'heading1'
    | 'heading2'
    | 'heading3'
    | 'heading4'
    | 'heading5'
    | 'heading6'
    | 'paragraph'
    | 'preformatted'
    | 'list'
    | 'oList'
    | 'listItem'
    | 'oListItem'
    | 'strong'
    | 'em'
    | 'hyperlink'
    | 'label'
  >
> = {
  heading1: ({ children }) => <h1>{children}</h1>,
  heading2: ({ children }) => <h2>{children}</h2>,
  heading3: ({ children }) => <h3>{children}</h3>,
  heading4: ({ children }) => <h4>{children}</h4>,
  heading5: ({ children }) => <h5>{children}</h5>,
  heading6: ({ children }) => <h6>{children}</h6>,
  paragraph: ({ children }) => <p>{children}</p>,
  preformatted: ({ children }) => <pre>{children}</pre>,
  list: ({ children }) => <ul>{children}</ul>,
  oList: ({ children }) => <ol>{children}</ol>,
  listItem: ({ children }) => <li>{children}</li>,
  oListItem: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => <strong>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  hyperlink: ({ children, node }) => <ContentLink field={node.data}>{children}</ContentLink>,
  label: ({ children }) => <span>{children}</span>,
}

/** Splits a text node's string at every span boundary and nests the active spans at each range — the standard rich text serialization. */
function renderSpans(text: string, spans: RTSpan[], components: RichTextComponents): ReactNode[] {
  if (!spans || spans.length === 0) return [text]

  const points = new Set<number>([0, text.length])
  for (const span of spans) {
    points.add(span.start)
    points.add(span.end)
  }
  const sorted = Array.from(points).sort((a, b) => a - b)

  const nodes: ReactNode[] = []
  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i]
    const end = sorted[i + 1]
    const chunk = text.slice(start, end)
    if (!chunk) continue

    const active = spans.filter((s) => s.start <= start && s.end >= end)
    let content: ReactNode = chunk
    for (let j = active.length - 1; j >= 0; j--) {
      const span = active[j]
      const Comp = ((components[span.type as keyof RichTextComponents] as SpanComponent | undefined) ??
        defaultComponents[span.type as keyof typeof defaultComponents]) as SpanComponent | undefined
      if (!Comp) continue
      content = (
        <Comp key={`${start}-${end}-${span.type}`} node={span}>
          {content}
        </Comp>
      )
    }
    nodes.push(content)
  }
  return nodes
}

function renderBlock(node: RTNode, key: string, components: RichTextComponents): ReactNode {
  if (node.type === 'image') {
    const Comp = components.image
    if (Comp) return <Comp key={key} node={node} />
    // biome-ignore lint/a11y/useAltText: alt comes from the field
    // biome-ignore lint/performance/noImgElement: default fallback rendering
    return (
      <img
        key={key}
        src={node.url}
        alt={node.alt ?? ''}
        width={node.dimensions.width}
        height={node.dimensions.height}
      />
    )
  }

  // The document `type` uses hyphenated names for list items ('list-item', 'o-list-item'),
  // but the components map keys them camelCase
  // ('listItem', 'oListItem') — normalize before the lookup or these silently fail to match.
  const kindMap: Partial<Record<RTNode['type'], keyof typeof defaultComponents>> = {
    'list-item': 'listItem',
    'o-list-item': 'oListItem',
  }
  const kind = (kindMap[node.type] ?? node.type) as keyof typeof defaultComponents
  const Comp = ((components[kind] as BlockComponent | undefined) ?? defaultComponents[kind]) as
    | BlockComponent
    | undefined
  if (!Comp) return null
  const children = renderSpans(node.text, node.spans, components)
  return (
    <Comp key={key} node={node}>
      {children}
    </Comp>
  )
}

export interface RichTextProps {
  field?: RichTextField | null
  components?: RichTextComponents
  fallback?: ReactNode
}

export function RichText({ field, components = {}, fallback = null }: RichTextProps) {
  if (!field || field.length === 0) return <>{fallback}</>

  const output: ReactNode[] = []
  let i = 0
  while (i < field.length) {
    const node = field[i]

    if (node.type === 'list-item' || node.type === 'o-list-item') {
      const isOrdered = node.type === 'o-list-item'
      const itemKind = isOrdered ? 'oListItem' : 'listItem'
      const WrapComp =
        (isOrdered ? components.oList : components.list) ??
        (isOrdered ? defaultComponents.oList : defaultComponents.list)
      const items: ReactNode[] = []
      let j = i
      while (j < field.length && field[j].type === node.type) {
        items.push(
          renderBlock(field[j], `item-${j}`, {
            ...components,
            [itemKind]: components[itemKind] ?? defaultComponents[itemKind],
          })
        )
        j++
      }
      output.push(<WrapComp key={`list-${i}`}>{items}</WrapComp>)
      i = j
      continue
    }

    output.push(renderBlock(node, `block-${i}`, components))
    i++
  }

  return <>{output}</>
}

// ---------------------------------------------------------------------------
// Slice zone
// ---------------------------------------------------------------------------

export interface SliceLike<SliceType extends string = string> {
  id?: string
  slice_type: SliceType
}

export interface SliceComponentProps<TSlice extends SliceLike = SliceLike, TContext = unknown> {
  slice: TSlice
  index: number
  slices: TSlice[]
  context: TContext
}

// biome-ignore lint/suspicious/noExplicitAny: the component map is deliberately permissive — each
// slice component has its own specific slice/context prop shape, which a single generic can't express.
export type SliceZoneComponents = Record<string, React.ComponentType<any>>

export interface SliceZoneProps<TSlice extends SliceLike = SliceLike, TContext = unknown> {
  slices?: TSlice[] | null
  components: SliceZoneComponents
  context?: TContext
}

export function SliceZone<TSlice extends SliceLike = SliceLike, TContext = unknown>({
  slices,
  components,
  context,
}: SliceZoneProps<TSlice, TContext>) {
  if (!slices || slices.length === 0) return null

  return (
    <>
      {slices.map((slice, index) => {
        // biome-ignore lint/suspicious/noExplicitAny: components map is keyed dynamically by slice_type
        const Comp = components[slice.slice_type] as any
        if (!Comp) return null
        const key = slice.id ?? `${slice.slice_type}-${index}`
        return <Comp key={key} slice={slice} index={index} slices={slices} context={context} />
      })}
    </>
  )
}
